import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR, formatApiError } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Printer, Pencil, Upload, Search, ArrowDownAZ, ArrowUpAZ } from "lucide-react";
import { toast } from "sonner";
import { dispatchPrintVoucher } from "@/lib/printDocuments";

const TYPES = ["Contra", "Payment", "Receipt", "Journal", "Sales", "Purchase", "Debit Note", "Credit Note"];
const SHORTCUTS = { Contra: "F4", Payment: "F5", Receipt: "F6", Journal: "F7", Sales: "F8", Purchase: "F9" };
// User pref: remember the last chosen order across sessions.
const SORT_LS_KEY = "tx_voucher_sort_dir";

function Inner() {
  const { activeId, active } = useCompany();
  const [filter, setFilter] = useState("");
  const [vouchers, setVouchers] = useState([]);
  const [q, setQ] = useState("");
  const [minAmt, setMinAmt] = useState("");
  const [maxAmt, setMaxAmt] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Sort direction — default DESCENDING (latest first) per user request.
  // Persisted to localStorage so power users don't need to re-set it every
  // time they land on the page.
  const [sortDir, setSortDir] = useState(() => {
    if (typeof window === "undefined") return "desc";
    return window.localStorage.getItem(SORT_LS_KEY) === "asc" ? "asc" : "desc";
  });
  const toggleSortDir = () => {
    setSortDir((prev) => {
      const next = prev === "desc" ? "asc" : "desc";
      try { window.localStorage.setItem(SORT_LS_KEY, next); } catch { /* private mode */ }
      return next;
    });
  };

  /**
   * Print a voucher as a Rule-46 compliant tax invoice.
   *
   * The invoice template needs three documents:
   *   • the voucher itself (already in `v`)
   *   • the supplier (active company) — name, address, GSTIN, signatory, …
   *   • the recipient (party ledger) — bill-to / ship-to / GSTIN, …
   *
   * We do the ledger fetch lazily here rather than eagerly in `load()` so we
   * don't fire N API calls when the user just scrolls the list.
   */
  const onPrint = useCallback(async (v) => {
    let partyLedger = null;
    if (v.party_ledger_id) {
      try {
        const { data } = await api.get(`/ledgers/${v.party_ledger_id}`);
        partyLedger = data;
      } catch (err) {
        // Ledger may have been deleted — still allow printing with snapshot
        // addresses captured on the voucher itself.
      }
    }
    dispatchPrintVoucher(v, { company: active || {}, partyLedger });
  }, [active]);

  const load = useCallback(async () => {
    if (!activeId) return;
    const params = filter ? { voucher_type: filter } : {};
    const { data } = await api.get(`/companies/${activeId}/vouchers`, { params });
    setVouchers(data);
    setSelected(new Set());
  }, [activeId, filter]);

  useEffect(() => { load(); }, [load]);

  // Client-side filtering + sort: text + amount range → then order by
  // (date, voucher_no) in the chosen direction. Sorting on the client keeps
  // the server payload simple and lets the toggle feel instant.
  const filtered = useMemo(() => {
    const lo = parseFloat(minAmt);
    const hi = parseFloat(maxAmt);
    const needle = q.trim().toLowerCase();
    const out = vouchers.filter((v) => {
      if (needle) {
        const blob = `${v.voucher_no} ${v.party_ledger_name || ""} ${v.narration || ""}`.toLowerCase();
        if (!blob.includes(needle)) return false;
      }
      const amt = Number(v.total) || 0;
      if (!Number.isNaN(lo) && minAmt !== "" && amt < lo) return false;
      if (!Number.isNaN(hi) && maxAmt !== "" && amt > hi) return false;
      return true;
    });
    // Stable ordering: (date, voucher_no) with voucher_no as tie-breaker so
    // same-date vouchers show up in a predictable sequence.
    out.sort((a, b) => {
      const d = (a.date || "").localeCompare(b.date || "");
      if (d !== 0) return d;
      return (a.voucher_no || "").localeCompare(b.voucher_no || "");
    });
    if (sortDir === "desc") out.reverse();
    return out;
  }, [vouchers, q, minAmt, maxAmt, sortDir]);

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((s) => s.size === filtered.length ? new Set() : new Set(filtered.map((v) => v.id)));
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete voucher ${v.voucher_no}?`)) return;
    try {
      await api.delete(`/vouchers/${v.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected voucher(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    let ok = 0, fail = 0;
    // Sequential to keep server pressure low + give clearer error report.
    for (const id of ids) {
      try {
        await api.delete(`/vouchers/${id}`);
        ok++;
      } catch (e) {
        fail++;
      }
    }
    setBulkDeleting(false);
    if (fail === 0) toast.success(`Deleted ${ok} voucher(s)`);
    else toast.error(`Deleted ${ok}, failed ${fail}`);
    load();
  };

  return (
    <>
      <PageHeader
        title="Vouchers"
        subtitle="All accounting entries — payments, receipts, journals, sales, purchases"
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" className="h-8" data-testid="btn-bulk-purchase">
              <Link to="/vouchers/bulk-purchase">
                <Upload className="w-4 h-4 mr-1.5" /> Bulk Purchase / Sales
              </Link>
            </Button>
            <Button asChild className="bg-blue-700 hover:bg-blue-800 h-8" data-testid="btn-bulk-upload">
              <Link to="/vouchers/bulk">
                <Upload className="w-4 h-4 mr-1.5" /> Upload Bulk Entries
              </Link>
            </Button>
          </div>
        }
      />
      <div className="p-6 space-y-4">
        <div className="flex flex-wrap gap-2">
          {TYPES.map((t) => (
            <Button
              key={t}
              asChild
              variant="outline"
              className="h-8 text-xs"
              data-testid={`btn-new-${t.toLowerCase().replace(" ", "-")}`}
            >
              <Link to={`/vouchers/new/${t}`}>
                + {t} {SHORTCUTS[t] && <kbd className="ml-1.5 text-[10px] font-mono bg-gray-100 border border-gray-200 rounded px-1">{SHORTCUTS[t]}</kbd>}
              </Link>
            </Button>
          ))}
        </div>

        <div className="flex gap-3 items-center flex-wrap">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 rounded border border-gray-300 px-2 text-sm" data-testid="select-voucher-filter">
            <option value="">All types</option>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-gray-400" />
            <Input
              placeholder="Search no / party / narration…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-8 pl-7 text-sm w-56"
              data-testid="input-voucher-search"
            />
          </div>
          <div className="flex items-center gap-1 text-xs text-gray-600">
            <span>Amount:</span>
            <Input
              type="number"
              placeholder="min"
              value={minAmt}
              onChange={(e) => setMinAmt(e.target.value)}
              className="h-8 text-xs w-24"
              data-testid="input-voucher-min-amt"
            />
            <span>—</span>
            <Input
              type="number"
              placeholder="max"
              value={maxAmt}
              onChange={(e) => setMaxAmt(e.target.value)}
              className="h-8 text-xs w-24"
              data-testid="input-voucher-max-amt"
            />
          </div>
          <button
            type="button"
            onClick={toggleSortDir}
            className="h-8 flex items-center gap-1.5 px-2.5 rounded border border-gray-300 bg-white text-xs text-gray-700 hover:bg-gray-50"
            data-testid="btn-toggle-sort-direction"
            aria-label={sortDir === "desc" ? "Sort by date descending (latest first)" : "Sort by date ascending (earliest first)"}
            title={sortDir === "desc"
              ? "Latest date first — click to switch to earliest first"
              : "Earliest date first — click to switch to latest first"}
          >
            {sortDir === "desc" ? (
              <ArrowDownAZ className="w-3.5 h-3.5 text-blue-700" />
            ) : (
              <ArrowUpAZ className="w-3.5 h-3.5 text-blue-700" />
            )}
            Date {sortDir === "desc" ? "↓ latest first" : "↑ earliest first"}
          </button>
          <span className="text-xs text-gray-500 font-mono ml-auto">
            {filtered.length} of {vouchers.length} voucher(s)
          </span>
          {selected.size > 0 && (
            <Button
              onClick={bulkDelete}
              disabled={bulkDeleting}
              variant="outline"
              className="h-8 text-xs text-red-700 border-red-200 hover:bg-red-50"
              data-testid="btn-bulk-delete-vouchers"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1" />
              {bulkDeleting ? "Deleting…" : `Delete ${selected.size} selected`}
            </Button>
          )}
        </div>

        <div className="border border-gray-200 rounded bg-white overflow-hidden">
          <table className="tx-table">
            <thead>
              <tr>
                <th className="w-10">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleAll}
                    data-testid="checkbox-select-all-vouchers"
                  />
                </th>
                <th>Date</th><th>No.</th><th>Type</th><th>Party / Narration</th>
                <th className="text-right">Amount</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-sm text-gray-400">
                  {vouchers.length === 0 ? "No vouchers" : "No vouchers match the filters"}
                </td></tr>
              )}
              {filtered.map((v) => (
                <tr key={v.id} data-testid={`row-voucher-${v.id}`}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(v.id)}
                      onChange={() => toggle(v.id)}
                      data-testid={`checkbox-voucher-${v.id}`}
                    />
                  </td>
                  <td className="font-mono text-xs">{v.date}</td>
                  <td className="font-mono">{v.voucher_no}</td>
                  <td>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{v.voucher_type}</span>
                    {v.doc_variant === "bill_of_supply" && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 ml-1"
                        data-testid={`badge-bill-of-supply-${v.id}`}
                      >
                        Bill of Supply
                      </span>
                    )}
                    {v.is_advance && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700 ml-1"
                        data-testid={`badge-advance-${v.id}`}
                      >
                        Advance
                      </span>
                    )}
                    {v.refund_against_voucher_no && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 ml-1"
                        data-testid={`badge-refund-${v.id}`}
                      >
                        Refund
                      </span>
                    )}
                  </td>
                  <td className="max-w-md truncate">{v.party_ledger_name || v.narration || "—"}</td>
                  <td className="tx-num">{formatINR(v.total)}</td>
                  <td className="text-right space-x-2 whitespace-nowrap">
                    <Link to={`/vouchers/edit/${v.id}`} className="text-gray-500 hover:text-blue-700 inline-block" title="Edit" data-testid={`btn-edit-${v.id}`}>
                      <Pencil className="w-4 h-4 inline" />
                    </Link>
                    <button onClick={() => onPrint(v)} className="text-gray-500 hover:text-gray-900" title="Print / Save as PDF" data-testid={`btn-print-${v.id}`}>
                      <Printer className="w-4 h-4 inline" />
                    </button>
                    <button onClick={() => remove(v)} className="text-red-600 hover:text-red-700" title="Delete" data-testid={`btn-delete-${v.id}`}>
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default function Vouchers() {
  return <RequireCompany><Inner /></RequireCompany>;
}
